import { Typography } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import IconButton from '@material-ui/core/IconButton';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import { ArrowExpandLeft, ArrowExpandRight } from 'mdi-material-ui';
import React, { createRef, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import FileIcon from './FileIcon';
import FileTreePanel from './FileTreePanel';
import Tooltip from '@material-ui/core/Tooltip';
import DiffFilesList from './DiffFilesList';
import LoadingIndicator from '../../../common/LoadingIndicator';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex'
    },
    toolbar: {
      marginBottom: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    textFieldInput: {
      paddingTop: 12,
      paddingBottom: 12
    },
    diffIcon: {
      marginRight: 4
    },
    fileTreeContainer: {
      width: 280,
      paddingRight: 8
    },
    diffPanelsContainer: {
      flex: 1
    },
    toolbarContent: {
      display: 'flex',
      alignItems: 'center'
    },
    statsContainer: {
      display: 'flex',
      alignItems: 'center'
    },
    statsItem: {
      display: 'flex',
      alignItems: 'center',
      marginRight: 8
    }
  })
);

interface Props {
  diffs: object;
  executionPlan: string;
  approvalDecisions: any[];
}

function filterFile(search, filename) {
  return !search || filename.toLowerCase().includes(search);
}

function DiffView(props: Props & RouteComponentProps<any>) {
  const { diffs, approvalDecisions, executionPlan } = props;
  const classes = useStyles({});
  const [refs, setRefs] = useState(null);
  const [stats, setStats] = useState(null);
  const [filteredDiffs, setFilteredDiffs] = useState(null);
  const [files, setFiles] = useState(null);
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [showAssignedFiles, setShowAssignedFiles] = useState(true);
  const [assignedApprovals, setAssignedApprovals] = useState(null);
  const [search, setSearch] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  useEffect(() => {
    // Get list of actions for current user approval
    const actionsAssignedToUser = approvalDecisions.reduce((accumulator, record) => {
      if (record.assignedToCurrentUser && record.actionName) {
        accumulator.add(record.actionName);
      }
      return accumulator;
    }, new Set());

    // If user is assigned to preapproval group then all actions are assigned
    const preApprovalAssigned = approvalDecisions.some(approvalDecision => approvalDecision.assignedToCurrentUser && !approvalDecision.actionName);

    setAssignedApprovals({
      actions: actionsAssignedToUser,
      preApprovalAssigned
    });
  }, [approvalDecisions]);

  useEffect(() => {
    if (diffs && assignedApprovals) {
      const normalizedSearch = searchFilter.toLowerCase();

      const filteredDiffs = { actions: [] } as any;
      if ((!showAssignedFiles || assignedApprovals.preApprovalAssigned) && filterFile(normalizedSearch, 'Workflow')) {
        filteredDiffs.workflow = diffs.workflow;
      }
      diffs.actions
        .filter(action => !showAssignedFiles || assignedApprovals.preApprovalAssigned || assignedApprovals.actions.has(action.actionName))
        .forEach(action => {
          const filteredAction = {
            actionName: action.actionName,
            deckName: action.deckName,
            deckVersion: action.deckVersion,
            diffType: action.diffType,
            deck: filterFile(normalizedSearch, action.deck.filename) ? action.deck : undefined,
            cloudFormation: action.cloudFormation.filter(({ filename }) => filterFile(normalizedSearch, filename)),
            files: action.files.filter(({ filename }) => filterFile(normalizedSearch, filename))
          };

          if (filteredAction.deck || filteredAction.cloudFormation.length || filteredAction.files.length) {
            filteredDiffs.actions.push(filteredAction);
          }
        });

      setFilteredDiffs(filteredDiffs);
    }
  }, [diffs, searchFilter, assignedApprovals, showAssignedFiles]);

  useEffect(() => {
    if (filteredDiffs && assignedApprovals) {
      // Build list of file diffs
      const files = [];

      if (filteredDiffs.workflow) {
        files.push({ ...filteredDiffs.workflow, fileType: 'Workflow', assigned: assignedApprovals.preApprovalAssigned });
      }

      filteredDiffs.actions.forEach(action => {
        const assigned = assignedApprovals.preApprovalAssigned || assignedApprovals.actions.has(action.actionName);
        if (action.deck) {
          files.push({
            ...action.deck,
            actionName: action.actionName,
            deckName: action.deckName,
            deckVersion: action.deckVersion,
            fileType: 'Deckspec',
            filePath: action.deckName,
            assigned
          });
        }
        action.cloudFormation.forEach(cf => {
          files.push({
            ...cf,
            actionName: action.actionName,
            deckName: action.deckName,
            deckVersion: action.deckVersion,
            fileType: 'CloudFormation',
            filePath: `${action.deckName} / CloudFormation`,
            assigned
          });
        });
        action.files.forEach(file => {
          files.push({
            ...file,
            actionName: action.actionName,
            deckName: action.deckName,
            deckVersion: action.deckVersion,
            fileType: 'File',
            filePath: `${action.deckName} / Files`,
            assigned
          });
        });
      });

      setFiles(files);
    }
  }, [filteredDiffs, assignedApprovals]);

  useEffect(() => {
    if (files) {
      setRefs(files.reduce((acc, value) => {
        acc[value.id] = createRef();
        return acc;
      }, {}));

      setStats(files.reduce((accumulator, file) => {
        accumulator[file.diffType]++;
        return accumulator;
      }, { added: 0, changed: 0, removed: 0 }));
    }
  }, [files]);

  function onFiledSelected(id) {
    if (refs && refs[id]) {
      setTimeout(() => {
        window.scrollTo(0, refs[id].current.offsetTop - 70);
      });
    }
  };

  function onSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSearch(event.target.value);
  }

  function onToggleFileBrowser() {
    setShowFileBrowser(!showFileBrowser);
  }

  function onAssignedFilesFilterChanged(event: React.ChangeEvent<HTMLInputElement>) {
    setShowAssignedFiles(event.target.checked);
  }

  function onKeyPress(event) {
    // Only handle enter key type
    if (event.which === 13) {
      setSearchFilter(search);
    }
  }

  return files && refs ? (
    <div className={classes.root}>
      {showFileBrowser && <div className={classes.fileTreeContainer}>
        <FileTreePanel diffs={filteredDiffs} executionPlan={executionPlan} onClick={onFiledSelected} />
      </div>}
      <div className={classes.diffPanelsContainer}>
        <div className={classes.toolbar}>
          <div className={classes.toolbarContent}>
            <IconButton onClick={onToggleFileBrowser}>
              {showFileBrowser && <ArrowExpandLeft />}
              {!showFileBrowser && <ArrowExpandRight />}
            </IconButton>
            {stats && <div className={classes.statsContainer}>
              {Object.keys(stats).map(key => <Tooltip key={key} title={`${stats[key]} file${stats[key] !== 1 ? 's' : ''} ${key}`}>
                <div className={classes.statsItem}>
                  <FileIcon diffType={key} className={classes.diffIcon} /><Typography>{stats[key]}</Typography>
                </div>
              </Tooltip>)}
            </div>}
          </div>
          <div className={classes.toolbarContent}>
            <Tooltip title="Only show files assigned to me">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAssignedFiles}
                    onChange={onAssignedFilesFilterChanged}
                    value="showOnlyMyChanges"
                    color="primary"
                  />
                }
                label="Assigned files only"
              />
            </Tooltip>
            <TextField
              id="search"
              value={search}
              margin="none"
              variant="outlined"
              placeholder="Search for file"
              inputProps={{ 'aria-label': 'bare', className: classes.textFieldInput }}
              onChange={onSearchChange}
              onKeyPress={onKeyPress}
            />
          </div>
        </div>
        <DiffFilesList files={files} refs={refs} />
      </div>
    </div>
  ) : <LoadingIndicator loading={true} />;
};

export default withRouter(DiffView);
